import { Button } from '@/ui/Button';
import { useBackToCollectionHref } from '@/features/course/learner/hooks/useBackToCollectionHref';
import { useBackToJourneyHref } from '@/features/course/learner/hooks/useBackToJourneyHref';

type BackToParentButtonProps = {
  courseId: number;
};

/**
 * Header back-navigation for the course start page.
 *
 * When the learner opened the course through a collection (the collection route
 * segments are present) it links back to that collection; otherwise, if the
 * course belongs to a journey, it links back to the journey. Renders nothing
 * when the course was opened via a direct link.
 *
 * Collection takes priority over journey because collection origin is
 * arrival-based (derived from the URL), whereas the journey breadcrumb is
 * membership-based. See PT-5574.
 */
export const BackToParentButton = ({ courseId }: BackToParentButtonProps): JSX.Element | null => {
  const backToCollectionHref = useBackToCollectionHref();
  const backToJourneyHref = useBackToJourneyHref({ id: courseId });

  const target = backToCollectionHref || backToJourneyHref;

  if (!target) {
    return null;
  }

  return (
    <Button to={target} variant="ghost" icon={['fas', 'arrow-left']} $iconPosition="start" size="large">
      {backToCollectionHref ? 'Back to collection' : 'Back to journey'}
    </Button>
  );
};
